import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  ArrowDown,
  ArrowUp,
  BarChartBig,
  CircleGauge,
  MessageCircle,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartIcon } from "../../charticons";
import {
  useConsumerSummary,
  useConsumptionUsage,
  useRevenueTrend,
  useServiceTrend,
} from "../../hooks";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";

interface DashboardVisualizationsProps {
  timeRange: "day" | "week" | "month" | "quarter" | "year";
  userRole: "manager" | "executive" | "callcenter" | "recovery";
  section: "primary" | "secondary" | "horizontal";
  consumerSummary: any;
  revenueTrendData: any;
  serviceTrendData: any;
  consumptionUsageData: any;
}

export function DashboardVisualizations({
  timeRange,
  userRole,
  section,
  consumerSummary,
  revenueTrendData,
  serviceTrendData,
  consumptionUsageData
}: DashboardVisualizationsProps) {
  const remoteUtilityId = getRemoteUtilityId();
  const [visualizationType, setVisualizationType] = useState<
    "revenue" | "services" | "usage"
  >("revenue");
  const [utilityService, setUtilityService] = useState<
    "water" | "electricity" | "gas"
  >("water");

  const summaryData = consumerSummary;
  // Transform API data for revenue trend chart
  const revenueData =
    revenueTrendData?.map((item) => ({
      name: item.month.split(" ")[0], // Extract month name (e.g., "Feb" from "Feb 2025")
      amount: item.totalAmount,
    })) || [];

  // Transform API data for revenue by category chart
  const revenueByCategory =
    revenueTrendData?.map((item) => {
      const baseData = {
        name: item.month.split(" ")[0], // Extract month name
        residential: 0,
        commercial: 0,
        industrial: 0,
      };

      // Map categories from API response
      item.categories.forEach((category) => {
        switch (category.category.toLowerCase()) {
          case "residential":
            baseData.residential = category.amount;
            break;
          case "commercial":
            baseData.commercial = category.amount;
            break;
          case "industrial":
            baseData.industrial = category.amount;
            break;
          case "na":
          default:
            baseData.residential = category.amount;
            break;
        }
      });

      return baseData;
    }) || [];

  // Transform API data for service trend chart
  const serviceData =
    serviceTrendData?.monthlyServiceData?.map((item) => ({
      name: item.month.split(" ")[0], // Extract month name (e.g., "Feb" from "Feb 2025")
      completed: item.completed,
      pending: item.inProgress, // Map in_progress to pending
    })) || [];

  // Transform API data for resolution time chart - keeping the original structure
  const resolutionTimeData =
    serviceTrendData?.priorityResolutionData?.map((item) => ({
      type: item.priority,
      hours: item.avgResolutionHours || 0, // Use 0 if null
    }));

  const usageData = consumptionUsageData?.result
    ? Object.entries(consumptionUsageData.result).map(([monthYear, data]) => {
        const consumptionData = data as { consumption: number };
        return {
          name: monthYear,
          residential: consumptionData.consumption,
          commercial: consumptionData.consumption,
          industrial: consumptionData.consumption,
        };
      })
    : [];

  // Role-specific visualization defaults
  const roleSpecificVisualizations = {
    manager: {
      primary: "revenue",
      secondary: "usage",
      horizontal: "revenue",
    },
    executive: {
      primary: "revenue",
      secondary: "services",
      horizontal: "revenue",
    },
    callcenter: {
      primary: "services",
      secondary: "usage",
      horizontal: "services",
    },
    recovery: {
      primary: "revenue",
      secondary: "usage",
      horizontal: "revenue",
    },
  };

  const visualizationDefaults = roleSpecificVisualizations[userRole][section];

  // KPI card data - would typically come from API
  const kpiCards = summaryData
    ? [
        {
          title: "Total Revenue",
          value: summaryData.totalRevenue?.value,
          trend: Math.abs(
            summaryData.totalRevenue?.percentageChange || 0
          ),
          trendDirection: summaryData.totalRevenue?.percentageChange,
          icon: <TrendingUp className="h-5 w-5 text-green-500" />,
          isPositiveGood: true,
        },
        {
          title: "Service Requests",
          value: summaryData.serviceRequests?.value,
          trend: Math.abs(
            summaryData.serviceRequests?.percentageChange || 0
          ),
          trendDirection: summaryData.serviceRequests?.percentageChange,
          icon: <BarChartBig className="h-5 w-5 text-blue-500" />,
          isPositiveGood: true,
        },
        {
          title: "Collection Rate",
          value: summaryData.collectionRate?.value,
          trend: Math.abs(
            summaryData.collectionRate?.percentageChange || 0
          ),
          trendDirection: summaryData.collectionRate?.percentageChange,
          icon: <CircleGauge className="h-5 w-5 text-indigo-500" />,
          isPositiveGood: true,
        },
        {
          title: "Active Complaints",
          value: summaryData.activeComplaints?.value,
          trend: Math.abs(
            summaryData.activeComplaints?.percentageChange || 0
          ),
          trendDirection: summaryData.activeComplaints?.percentageChange,
          icon: <MessageCircle className="h-5 w-5 text-orange-500" />,
          isPositiveGood: false, // For complaints, down is good
        },
      ]
    : [];

  // Horizontal layout with 3 cards
  if (section === "horizontal") {
    return (
      <Card className="w-full">
        <CardHeader className="pb-2">
          <CardTitle className="page-subheading flex items-center justify-between">
            Analytics & Insights
            <ChartIcon type={visualizationType} />
          </CardTitle>
        </CardHeader>

        <CardContent className="p-4">
          {/* KPI Cards at the top */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {kpiCards.map((card, index) => (
                <QuickStatsCard
                  title={card.title}
                  count={card.value}
                  trendSummary={`${card.trend}% from last ${timeRange}`}
                  icon={card.icon}
                />
            ))}
          </div>

          <Tabs
            defaultValue={visualizationDefaults}
            tabComponents={{
              revenue: {
                label: "Revenue",
                component: (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-card rounded-md p-3 border overflow-hidden">
                        <h3 className="text-sm font-medium mb-2">
                          Revenue Trend
                        </h3>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={revenueData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Line
                                type="monotone"
                                dataKey="amount"
                                stroke="#2563eb"
                                name="Revenue"
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="bg-card rounded-md p-3 border overflow-hidden ">
                        <h3 className="text-sm font-medium mb-2">
                          Revenue by Consumer Category
                        </h3>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={revenueByCategory}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar
                                dataKey="residential"
                                fill="#2563eb"
                                name="Residential"
                              />
                              <Bar
                                dataKey="commercial"
                                fill="#10b981"
                                name="Commercial"
                              />
                              <Bar
                                dataKey="industrial"
                                fill="#f59e0b"
                                name="Industrial"
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              services: {
                label: "Services",
                component: (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-card rounded-md p-3 border overflow-hidden">
                        <h3 className="text-sm font-medium mb-2">
                          Service Requests
                        </h3>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={serviceData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar
                                dataKey="completed"
                                fill="#16a34a"
                                name="Completed"
                              />
                              <Bar
                                dataKey="pending"
                                fill="#f59e0b"
                                name="Pending"
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <div className="bg-card rounded-md p-3 border overflow-hidden">
                        <h3 className="text-sm font-medium mb-2">
                          Resolution Time
                        </h3>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={resolutionTimeData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="type" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar
                                dataKey="hours"
                                fill="#6366f1"
                                name="Hours"
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              usage: {
                label: "Usage",
                component: (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      <div className="bg-card rounded-md p-3 border overflow-hidden">
                        <h3 className="text-sm font-medium mb-2">
                          {utilityService.charAt(0).toUpperCase() +
                            utilityService.slice(1)}{" "}
                          Usage by Segment
                        </h3>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={usageData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar
                                dataKey="residential"
                                fill="#3b82f6"
                                name="Residential"
                              />
                              <Bar
                                dataKey="commercial"
                                fill="#8b5cf6"
                                name="Commercial"
                              />
                              <Bar
                                dataKey="industrial"
                                fill="#f59e0b"
                                name="Industrial"
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
            }}
            tabsListClassName="grid grid-cols-3 mb-4"
            className="space-y-4"
            disableUrlSync={true}
          />
        </CardContent>
      </Card>
    );
  }
}
